import { useContext, useState } from "react";
import { Card, InputNumber, Typography, Flex, Button, message, Select, Tag, Radio } from "antd";
import { HarvestContext, HarvestUIContext } from "./HarvestContext.js";
import {
  getOptimisedHarvestPlan,
  setHarvestConstraintsToSupabase,
  recomputeHarvestPlan,
} from "./harvestOptimiser.service.js";
import { updateSiteMetadata } from "../../../utils/api.js";
import { BACKEND_DATE_FORMAT, getSiteTimezone, siteDayjs } from "./siteTimezone.js";
import { FORECAST_WINDOW_DAYS } from "./harvestOptimiser.utils.js";
import { convertHaToAcre, convertAcreToHa, getAreaUnitLabel } from "../../../utils/unitConversions.js";
import { convertKgToPound, convertPoundToKg, getWeightUnitLabel } from "../../../utils/poundConversions.js";

const { Text } = Typography;

function UnavailableDaysConstraint({ unavailableDates, setUnavailableDates, siteTimezone }) {
  const dateRange = (() => {
    const today = siteDayjs(null, siteTimezone).startOf("day");
    const dates = [];
    for (let i = 0; i < FORECAST_WINDOW_DAYS; i++) {
      dates.push(today.add(i, "day"));
    }
    return dates;
  })();

  const options = dateRange.map((date) => {
    return {
      value: date.format(BACKEND_DATE_FORMAT),
      label: date.format("ddd MMM D"),
    };
  });

  return (
    <Flex vertical gap={8} className="w-full">
      <Text strong>Unavailable Days</Text>
      <Select
        mode="multiple"
        placeholder="Select unavailable dates"
        value={unavailableDates}
        onChange={setUnavailableDates}
        options={options}
        className="w-full"
        tagRender={(props) => (
          <Tag color="red" onClose={props.onClose} style={{ marginRight: 3 }}>
            {props.label}
          </Tag>
        )}
      />
    </Flex>
  );
}

function WeightConstraint({ weightMode, setWeightMode, weightKg, setWeightKg, binKgPerUnit, setBinKgPerUnit, binCount, setBinCount, existingWeightKg, displayUnits }) {
  return (
    <Flex vertical gap={12} className="w-full">
      <Flex gap={16} align="center">
        <Text strong>Weigh in</Text>
        <Radio.Group
          value={weightMode}
          onChange={(e) => setWeightMode(e.target.value)}
          options={
            displayUnits === "imperial"
              ? [
                  { label: 'Pounds', value: 'weight' },
                  { label: 'Bins', value: 'bin' },
                ]
              : [
                  { label: 'Kg', value: 'weight' },
                  { label: 'Bins', value: 'bin' },
                ]
          }
        />
      </Flex>

      {weightMode === 'weight' && (
        <Flex vertical gap={8}>
          <Text className="text-sm">Maximum Weight / Day</Text>
          <InputNumber
            value={
              weightKg != null ? weightKg : (displayUnits === "imperial" && existingWeightKg != null ? convertKgToPound(existingWeightKg) : existingWeightKg)
            }
            suffix={` ${getWeightUnitLabel(displayUnits)} `}
            className="w-full"
            mode="spinner"
            onChange={(value) => {
              setWeightKg(value);
            }}
            min={displayUnits === "imperial" ? 2 : 1}
            step={1}
            placeholder={displayUnits === "imperial" ? "Enter weight in lb (min 2)" : "Enter weight in kg"}
          />
        </Flex>
      )}

      {weightMode === 'bin' && (
        <Flex vertical gap={12}>
          <Flex vertical gap={8}>
            <Text className="text-sm">Kg/Bin</Text>
            <InputNumber
              value={binKgPerUnit}
              suffix={" kg "}
              className="w-full"
              mode="spinner"
              onChange={(value) => setBinKgPerUnit(value)}
              min={1}
              step={1}
              placeholder="Enter kg per bin"
            />
            {displayUnits === "imperial" && (
              <Text type="secondary" className="text-xs">(Always in kg, regardless of site units)</Text>
            )}
          </Flex>

          <Flex vertical gap={8}>
            <Text className="text-sm">Number of bins</Text>
            <InputNumber
              value={binCount}
              className="w-full"
              mode="spinner"
              onChange={(value) => setBinCount(value)}
              min={1}
              step={1}
              placeholder="Enter bin count"
            />
          </Flex>

          {binKgPerUnit && binCount && (
            <Text type="secondary" className="text-sm bg-blue-50 p-2 rounded">
              Maximum Weight / Day: {displayUnits === "imperial" ? convertKgToPound(Math.floor(binKgPerUnit * binCount)) : Math.floor(binKgPerUnit * binCount)} {getWeightUnitLabel(displayUnits)}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export function HarvestConstraints({ onClose }) {
  const context = useContext(HarvestContext);
  const uiContext = useContext(HarvestUIContext);
  const areaUnit = uiContext?.areaUnit ?? "Hectare";
  const displayUnits = uiContext?.displayUnits ?? "metric";
  const siteConfig = context?.siteConfig ?? null;
  const siteMetaData = context?.siteMetaData ?? null;
  const show_harvest_constraints = siteMetaData?.broccoli?.OPTIMISER?.config?.show_harvest_constraints ?? null;

  const show_area_per_day = show_harvest_constraints?.area_per_day ?? null;
  const show_blocks_per_day = show_harvest_constraints?.blocks_per_day ?? null;
  const show_locations_per_day = show_harvest_constraints?.locations_per_day ?? null;
  const show_maximum_GDD = show_harvest_constraints?.maximum_GDD ?? null;
  const show_maximum_weight = show_harvest_constraints?.maximum_weight ?? null;
  const show_minimum_weight = show_harvest_constraints?.minimum_weight ?? null;
  const show_non_harvest_dates = show_harvest_constraints?.non_harvest_dates ?? null;

  const [loading, setLoading] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [weightMode, setWeightMode] = useState('weight');
  const [weightKg, setWeightKg] = useState(null);
  const [binKgPerUnit, setBinKgPerUnit] = useState(null);
  const [binCount, setBinCount] = useState(null);

  const [eventPerDayOverride, setEventPerDay] = useState(null);
  const [areaPerDayOverride, setAreaHarvestablePerDay] = useState(null);
  const [maxLocationOverride, setMaxLocationPerDay] = useState(null);
  const [maximumGddOverride, setMaximumGdd] = useState(null);
  const [minimumWeightOverride, setMinimumWeight] = useState(null);

  const eventPerDay = eventPerDayOverride ?? siteConfig?.event_per_day ?? 1;
  const areaHarvestablePerDay = areaPerDayOverride ?? siteConfig?.area_harvestable_per_day_ha ?? 1;
  const maxLocationPerDay = maxLocationOverride ?? siteConfig?.max_location_per_day ?? 1;
  const maximumGdd = maximumGddOverride ?? siteConfig?.maximum_cumulative_gdd ?? 1;
  const maximumWeight = siteConfig?.maximum_weight_per_day_in_kg ?? 1;
  const minimumWeight = minimumWeightOverride ?? siteConfig?.minimum_weight_per_harvest_pass ?? 1;
  const siteTimezone = getSiteTimezone(siteConfig, context?.currentSite);

  // Check if any constraint is available
  const hasAnyConstraint = show_area_per_day || show_blocks_per_day || show_locations_per_day || show_maximum_GDD || show_maximum_weight || show_minimum_weight || show_non_harvest_dates;

  const buildConstraints = () => {
    let finalWeightPerDay = maximumWeight;

    if (weightMode === 'bin' && binKgPerUnit != null && binCount != null) {
      finalWeightPerDay = Math.floor(binKgPerUnit * binCount);
    } else if (weightMode === 'weight' && weightKg != null) {
      // weightKg state stores in display units; convert to kg if imperial, clamp to min 1 kg
      const convertedKg = displayUnits === "imperial" ? convertPoundToKg(weightKg, { round: false }) : weightKg;
      finalWeightPerDay = Math.max(1, convertedKg);
    }

    let finalMinimumWeight = minimumWeight;
    if (minimumWeightOverride != null) {
      const convertedMinKg = displayUnits === "imperial" ? convertPoundToKg(minimumWeightOverride, { round: false }) : minimumWeightOverride;
      finalMinimumWeight = Math.max(1, convertedMinKg);
    }

    return {
      events_per_day: eventPerDay,
      area_harvestable_per_day_ha: areaHarvestablePerDay,
      max_location_per_day: maxLocationPerDay,
      max_pass_gap_days: siteConfig?.harvest_pass_gap,
      maximum_cumulative_gdd: maximumGdd,
      maximum_weight_per_day_in_kg: finalWeightPerDay,
      minimum_weight_per_harvest_pass: finalMinimumWeight,
      non_harvest_dates: unavailableDates && unavailableDates.length > 0 ? unavailableDates : [],
    };
  };

  const previewHarvestPlan = async () => {
    setLoading(true);
    try {
      const constraints = buildConstraints();
      const data = await getOptimisedHarvestPlan(
        context.currentOrg,
        context.currentSite.id,
        constraints,
      );
      context.setRecommendedHarvestPlan(data);
      await recomputeHarvestPlan(context.currentOrg, context.currentSite.id);
      onClose?.();
    } catch (error) {
      console.error("Error previewing harvest plan:", error);
      message.error("Failed to preview harvest plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const prepareHarvestPlan = async () => {
    setLoading(true);
    try {
      const constraints = buildConstraints();
      const data = await getOptimisedHarvestPlan(
        context.currentOrg,
        context.currentSite.id,
        constraints,
      );
      context.setRecommendedHarvestPlan(data);
      await recomputeHarvestPlan(context.currentOrg, context.currentSite.id);
      await setHarvestConstraintsToSupabase(context.currentSite.name, constraints);

      const metadataUpdate = {
        ...(siteMetaData ?? {}),
        broccoli: {
          ...(siteMetaData?.broccoli ?? {}),
          OPTIMISER: {
            ...(siteMetaData?.broccoli?.OPTIMISER ?? {}),
            config: {
              ...(siteMetaData?.broccoli?.OPTIMISER?.config ?? {}),
              weigh_mode: {
                ...(siteMetaData?.broccoli?.OPTIMISER?.config?.weigh_mode ?? {}),
                mode: weightMode,
                ...(weightKg != null ? { weight_kg: displayUnits === "imperial" ? Math.max(1, convertPoundToKg(weightKg, { round: false })) : weightKg } : {}),
                ...(binKgPerUnit != null ? { bin_kg_per_unit: binKgPerUnit } : {}),
                ...(binCount != null ? { bin_count: binCount } : {}),
              },
              non_harvest_dates: unavailableDates && unavailableDates.length > 0 ? unavailableDates : (siteMetaData?.broccoli?.OPTIMISER?.config?.non_harvest_dates ?? []),
            },
          },
        },
      };

      const updateRes = await updateSiteMetadata(context.currentSite.id, {
        metadata: metadataUpdate,
      });

      if (updateRes?.status !== 200) {
        throw new Error("Failed to update site metadata.");
      }

      message.success('Harvest plan prepared and constraints saved.');
      onClose?.();
    } catch (error) {
      console.error("Error preparing harvest plan:", error);
      message.error("Failed to prepare harvest plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Harvest Constraints" size="small">
      {!hasAnyConstraint ? (
        <Flex vertical gap={32} align="center" justify="center" className="w-full py-8">
          <Text type="secondary">No constraints available</Text>
        </Flex>
      ) : (
      <Flex vertical gap={32} align="start" className="w-full">
        {show_non_harvest_dates && (
          <UnavailableDaysConstraint
            unavailableDates={unavailableDates}
            setUnavailableDates={setUnavailableDates}
            siteTimezone={siteTimezone}
          />
        )}

        <WeightConstraint
          weightMode={weightMode}
          setWeightMode={setWeightMode}
          weightKg={weightKg}
          setWeightKg={setWeightKg}
          binKgPerUnit={binKgPerUnit}
          setBinKgPerUnit={setBinKgPerUnit}
          binCount={binCount}
          setBinCount={setBinCount}
          existingWeightKg={show_maximum_weight ? maximumWeight : null}
          displayUnits={displayUnits}
        />

        {show_blocks_per_day && (
          <Flex vertical gap={8} className="w-full">
            <Text strong>Blocks / Day</Text>
            <InputNumber
              value={eventPerDay}
              suffix={"blocks\u00A0"}
              className="w-full"
              mode="spinner"
              onChange={(value) => setEventPerDay(value)}
              min={1}
            />
          </Flex>
        )}

        {show_area_per_day && (
          <Flex vertical gap={8} className="w-full">
            <Text strong>Area / Day</Text>
            {(() => {
              const minHa = 1;
              const stepHa = 0.05;
              const minValue = areaUnit === "acre" ? convertHaToAcre(minHa) : minHa;
              const stepValue = areaUnit === "acre" ? convertHaToAcre(stepHa) : stepHa;
              return (
                <InputNumber
                  value={
                    areaHarvestablePerDay != null && !isNaN(areaHarvestablePerDay)
                      ? (areaUnit === "acre" ? convertHaToAcre(areaHarvestablePerDay) : areaHarvestablePerDay)
                      : null
                  }
                  suffix={`${getAreaUnitLabel(areaUnit)} `}
                  className="w-full"
                  mode="spinner"
                  precision={2}
                  onChange={(value) => {
                    // Filter null/NaN before conversion
                    if (value == null || isNaN(value)) {
                      setAreaHarvestablePerDay(null);
                      return;
                    }
                    const valueInHa = areaUnit === "acre" ? convertAcreToHa(value) : value;
                    setAreaHarvestablePerDay(valueInHa);
                  }}
                  min={minValue}
                  step={stepValue}
                />
              );
            })()}
          </Flex>
        )}

        {show_locations_per_day && (
          <Flex vertical gap={8} className="w-full">
            <Text strong>Locations / Day</Text>
            <InputNumber
              value={maxLocationPerDay}
              suffix={"locations\u00A0"}
              className="w-full"
              mode="spinner"
              onChange={(value) => setMaxLocationPerDay(value)}
              min={1}
            />
          </Flex>
        )}

        {show_maximum_GDD && (
          <Flex vertical gap={8} className="w-full">
            <Text strong>Maximum GDD</Text>
            <InputNumber
              value={maximumGdd}
              className="w-full"
              mode="spinner"
              onChange={(value) => setMaximumGdd(value)}
              min={1}
              max={10000}
            />
          </Flex>
        )}


        {show_minimum_weight && (
          <Flex vertical gap={8} className="w-full">
            <Text strong>Minimum Weight / Harvest Pass</Text>
            <InputNumber
              value={
                minimumWeightOverride != null ? minimumWeightOverride : (displayUnits === "imperial" && siteConfig?.minimum_weight_per_harvest_pass != null ? convertKgToPound(siteConfig.minimum_weight_per_harvest_pass) : siteConfig?.minimum_weight_per_harvest_pass)
              }
              suffix={` ${getWeightUnitLabel(displayUnits)} `}
              className="w-full"
              mode="spinner"
              onChange={(value) => {
                setMinimumWeight(value);
              }}
              min={displayUnits === "imperial" ? 2 : 1}
              step={1}
            />
          </Flex>
        )}
        {hasAnyConstraint && (
          <Flex vertical gap={24} className="w-full">
            <Button
              color="primary"
              variant="outlined"
              block
              onClick={previewHarvestPlan}
              loading={loading}
            >
              Preview Harvest Plan
            </Button>
            <Button type="primary" block onClick={prepareHarvestPlan} loading={loading}>
              Prepare New Harvest Plan
            </Button>
          </Flex>
        )}
      </Flex>
      )}
    </Card>
  );
}
